import React, { useCallback, useContext, useReducer, useRef, useEffect, useState } from 'react';
import { ApkContext, TCountOfApk, TApkSortBy, TTypeOfModalWindow, apkInitialState, IFilterValues } from './apkContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { apkReducer } from './apkReducer';
import { ApkActionTypes } from './apkActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedApk, deleteApk } from 'backend/apk';
import axios, { AxiosResponse } from 'axios';
import {
	IDeleteApkFirmwareValidationError,
	IServerError,
	ServerErrorTypes,
	IApk,
	IGetPaginatedApk,
	TApkType,
} from 'types';

const ApkState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(apkReducer, apkInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IApk>, IGetPaginatedApk>(
		isMounted.current ? getPaginatedApk : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			type: state.IDSelectedApkType,
			isUnassignedTabletApps: state.isUnassignedApk,
			sortByName: state.sortBy === 'VersionName1-10' || state.sortBy === 'VersionName10-1' ? state.sortBy : null,
			sortByDescription:
				state.sortBy === 'DescriptionA-Z' || state.sortBy === 'DescriptionZ-A' ? state.sortBy : null,
			sortByApplicationId:
				state.sortBy === 'ApplicationId1-10' || state.sortBy === 'ApplicationId10-1' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ApkActionTypes.SET_APK,
				payload: {
					apk: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteApk(state.apkIDForDeletion);
			const filteredApk = state.apk.items.filter((el) => el.id !== state.apkIDForDeletion);
			dispatch({
				type: ApkActionTypes.SET_APK,
				payload: {
					apk: {
						count: state.apk.count - 1,
						items: filteredApk,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			if (axios.isAxiosError(err) && err.response?.data.Type === ServerErrorTypes.TABLET_APP_DELETION_FAILED) {
				dispatch({
					type: ApkActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
					payload: {
						errorResponseData: err.response.data,
						typeOfModalWindow: null,
					},
				});
			} else {
				dispatch({
					type: ApkActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'error',
					},
				});
			}
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: ApkActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const setIDApkType = (value: Nullable<TApkType>) => {
		dispatch({
			type: ApkActionTypes.SET_ID_APK_TYPE,
			payload: {
				IDSelectedApkType: value,
			},
		});
	};

	const toggleUnassignedApk = () => {
		dispatch({ type: ApkActionTypes.TOGGLE_UNASSIGNED_APK });
	};

	const resetSelectedControls = () => dispatch({ type: ApkActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (value: TApkSortBy) => {
		dispatch({
			type: ApkActionTypes.SET_SORT_BY,
			payload: {
				sortBy: value,
			},
		});
	};

	const setCountOfItems = (value: TCountOfApk) => {
		dispatch({
			type: ApkActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: value,
			},
		});
	};

	const changeCurrentPage = (value: number) => {
		dispatch({
			type: ApkActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: value,
			},
		});
	};

	const setApkIDForDeletion = (value: string) => {
		dispatch({
			type: ApkActionTypes.SET_APK_ID_FOR_DELETION,
			payload: {
				apkIDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: ApkActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const showErrorModalWithInfo = useCallback((value: IServerError<IDeleteApkFirmwareValidationError> | null) => {
		dispatch({
			type: ApkActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
				typeOfModalWindow: null,
			},
		});
	}, []);

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: ApkActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<ApkContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setIDApkType,
				toggleUnassignedApk,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setApkIDForDeletion,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				showErrorModalWithInfo,
				apk: state.apk,
				searchString: state.searchString,
				IDSelectedApkType: state.IDSelectedApkType,
				isUnassignedApk: state.isUnassignedApk,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				apkIDForDeletion: state.apkIDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</ApkContext.Provider>
	);
};

export default ApkState;
